export type QueryValue = string | number | boolean | undefined | null | string[];
export type QueryParams = Record<string, QueryValue>;

/** Appends query params to a URLSearchParams; arrays are repeated (`a=1&a=2`), null/undefined are skipped. */
export function appendQuery(search: URLSearchParams, query: QueryParams | undefined): void {
  if (!query) return;
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const v of value) search.append(key, v);
    } else {
      search.append(key, String(value));
    }
  }
}

function isAbsolute(url: string): boolean {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
}

/**
 * Joins base URL and path (handles duplicate/missing slashes) and serialises query params.
 * Absolute paths (`https://...`) bypass the base URL.
 */
export function buildUrl(baseUrl: string, path: string, query?: QueryParams): string {
  let url: string;
  if (isAbsolute(path) || !baseUrl) {
    url = path;
  } else {
    const base = baseUrl.replace(/\/+$/, '');
    const rel = path.startsWith('/') ? path : `/${path}`;
    url = `${base}${rel}`;
  }
  if (!query) return url;
  const search = new URLSearchParams();
  appendQuery(search, query);
  const qs = search.toString();
  if (!qs) return url;
  return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;
}

/** Merges an optional user signal with an optional timeout into a single AbortSignal. */
export function combineSignals(signal: AbortSignal | undefined, timeoutMs: number | undefined): AbortSignal | undefined {
  const signals: AbortSignal[] = [];
  if (signal) signals.push(signal);
  if (timeoutMs !== undefined && timeoutMs > 0) {
    if (typeof AbortSignal.timeout === 'function') {
      signals.push(AbortSignal.timeout(timeoutMs));
    } else {
      const controller = new AbortController();
      setTimeout(() => controller.abort(new DOMException('The operation timed out', 'TimeoutError')), timeoutMs);
      signals.push(controller.signal);
    }
  }
  if (signals.length === 0) return undefined;
  if (signals.length === 1) return signals[0];
  if (typeof AbortSignal.any === 'function') return AbortSignal.any(signals);
  const controller = new AbortController();
  for (const s of signals) {
    if (s.aborted) {
      controller.abort(s.reason);
      break;
    }
    s.addEventListener('abort', () => controller.abort(s.reason), { once: true });
  }
  return controller.signal;
}
